import { useUser } from "@dashboard/auth";
import { getApiUrl } from "@dashboard/config";
import {
  PrepareCheckoutCreateDocument,
  useBaseChannelsQuery,
  usePrepareCheckoutCustomerAttachMutation,
  useUpdatePrivateMetadataMutation,
} from "@dashboard/graphql";
import { useState } from "react";
import { useIntl } from "react-intl";

import { prepareCheckoutMessages as messages } from "./messages";

/** Marker the storefront and the quotes app read to know a staff member built this cart. */
export const PREPARED_BY_METADATA_KEY = "wsm.prepared_by";

/** The channel a cart is priced on unless the tenant has exactly one other. */
export const DEFAULT_CHANNEL_SLUG = "default-channel";

export interface PrepareCheckoutCustomer {
  id: string;
  email?: string | null;
}

export type PrepareCheckoutDisabledReason = "no-storefront" | "no-email" | "no-channel" | null;

export const getStorefrontOrigin = (): string =>
  process.env.NEXT_PUBLIC_STOREFRONT_BASE_URL?.trim().replace(/\/+$/, "") ?? "";

const firstMessage = (errors: Array<{ message?: string | null }> | undefined | null) =>
  errors?.find(error => !!error?.message)?.message ?? null;

/** A cart that exists in Saleor with a step still owed to it. */
interface PendingCheckout {
  id: string;
  /** Whose email is baked into that cart. checkoutCustomerAttach rebinds the
   *  user but never the email, so another customer needs another cart. */
  customerId: string;
  step: "attach" | "stamp";
}

export interface UsePrepareCheckout {
  prepare: () => Promise<void>;
  loading: boolean;
  error: string | null;
  disabledReason: PrepareCheckoutDisabledReason;
  channelSlug: string;
  /** The cart's currency, from the tenant channel it is priced on. */
  currencyCode: string;
  isNonDefaultChannel: boolean;
  /** The cart the last prepare finished, which the rep quotes freight on. */
  preparedCheckoutId: string | null;
}

/**
 * Builds an empty cart on a customer's account and opens the storefront on it.
 * Four steps, in order, and the auth on each one matters:
 *   1. checkoutCreate  UNAUTHENTICATED, in the tenant's channel, with the customer's email
 *   2. checkoutCustomerAttach  staff token (IMPERSONATE_USER)
 *   3. updatePrivateMetadata  staff token, stamps who prepared it
 *   4. open <storefront origin>/prepare?checkout=<id>
 * The dashboard never mints a pay link: it only builds the cart.
 *
 * The channel is a property of the TENANT, never of the staff member's channel
 * picker: a cart priced on whichever channel an operator last looked at is a
 * money bug. Nothing today records a channel on the customer either, so a dealer
 * cart waits on the dealer rebuild in PartsLogic Search.
 *
 * A step that fails after the cart exists keeps the cart id: the next click
 * resumes at the failed step instead of orphaning a second cart on the tenant.
 */
export const usePrepareCheckout = (
  customer?: PrepareCheckoutCustomer | null,
): UsePrepareCheckout => {
  const intl = useIntl();
  const { user } = useUser();
  // Already in the Apollo cache: the app shell runs this same query on mount.
  const { data: channelsData } = useBaseChannelsQuery();
  const [attachCustomer] = usePrepareCheckoutCustomerAttachMutation();
  const [stampPreparedBy] = useUpdatePrivateMetadataMutation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingCheckout | null>(null);
  const [prepared, setPrepared] = useState<{ id: string; customerId: string } | null>(null);
  const storefrontOrigin = getStorefrontOrigin();
  const channels = channelsData?.channels ?? [];
  const channelSlug =
    channels.find(channel => channel.slug === DEFAULT_CHANNEL_SLUG)?.slug ??
    (channels.length === 1 ? channels[0].slug : "");
  const currencyCode = channels.find(channel => channel.slug === channelSlug)?.currencyCode ?? "";
  const disabledReason: PrepareCheckoutDisabledReason = !storefrontOrigin
    ? "no-storefront"
    : !customer?.email
      ? "no-email"
      : !channelSlug
        ? "no-channel"
        : null;
  const prepare = async () => {
    if (disabledReason || !customer?.email) {
      return;
    }

    setLoading(true);
    setError(null);

    const resumable = pending?.customerId === customer.id ? pending : null;

    let checkoutId = resumable?.id ?? "";

    try {
      if (!checkoutId) {
        // Step 1: no Authorization header on this one call, on purpose. A checkout
        // created as the staff user belongs to the staff user, and Saleor then
        // refuses to reassign it.
        const response = await fetch(getApiUrl(), {
          method: "POST",
          credentials: "omit",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            query: PrepareCheckoutCreateDocument.loc?.source.body,
            variables: { channel: channelSlug, email: customer.email },
          }),
        });
        const payload = await response.json();

        checkoutId = payload?.data?.checkoutCreate?.checkout?.id ?? "";

        if (!checkoutId) {
          setError(
            firstMessage(payload?.data?.checkoutCreate?.errors) ??
              firstMessage(payload?.errors) ??
              intl.formatMessage(messages.createFailed),
          );

          return;
        }
      }

      // Step 2: staff token, the mutation IMPERSONATE_USER gates.
      if (resumable?.step !== "stamp") {
        const attached = await attachCustomer({
          variables: { id: checkoutId, customerId: customer.id },
        });
        const attachError = firstMessage(attached.data?.checkoutCustomerAttach?.errors);

        if (attachError) {
          setPending({ id: checkoutId, customerId: customer.id, step: "attach" });
          setError(
            intl.formatMessage(messages.stepFailed, { message: attachError, cart: checkoutId }),
          );

          return;
        }
      }

      // Step 3: staff token, mark who built the cart.
      const stamped = await stampPreparedBy({
        variables: {
          id: checkoutId,
          input: [{ key: PREPARED_BY_METADATA_KEY, value: user?.email ?? "" }],
          keysToDelete: [],
        },
      });
      const stampError = firstMessage(stamped.data?.updatePrivateMetadata?.errors);

      if (stampError) {
        setPending({ id: checkoutId, customerId: customer.id, step: "stamp" });
        setError(
          intl.formatMessage(messages.stepFailed, { message: stampError, cart: checkoutId }),
        );

        return;
      }

      setPending(null);
      setPrepared({ id: checkoutId, customerId: customer.id });

      // Step 4: hand the cart to the storefront. `noopener` in the feature string
      // would make window.open return null by spec, which reads as "blocked", so
      // the opener is cut on the handle instead.
      const url = `${storefrontOrigin}/prepare?checkout=${encodeURIComponent(checkoutId)}`;
      const tab = window.open(url, "_blank");

      if (tab) {
        tab.opener = null;
      } else {
        setError(intl.formatMessage(messages.popupBlocked, { url }));
        window.location.assign(url);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setLoading(false);
    }
  };

  return {
    prepare,
    loading,
    error,
    disabledReason,
    channelSlug,
    currencyCode,
    isNonDefaultChannel: !!channelSlug && channelSlug !== DEFAULT_CHANNEL_SLUG,
    // Another customer picked in the dialog is another cart: never quote freight onto the last one.
    preparedCheckoutId: prepared && prepared.customerId === customer?.id ? prepared.id : null,
  };
};
